// Le travail actif : le clic, l'énergie et la fatigue.
import { round2 } from '../engine/finance';
import { fmtEur } from '../engine/format';
import type { GameState } from '../engine/state';
import type { TextKey } from '../content/fr';
import { addJournal, addWellbeing, applyEnergyDrop, unlockPage } from './actions';
import { MONTHLY_HOURS, OVERTIME_BONUS, recordIncome } from './economy';
import { startEvent } from './events';
import { maybeSummerTemptation } from './temptations';
import { CAR } from './car';
import { upgradeBonus } from './upgrades';
import { checkExhaustion, currentMood } from './wellbeing';

export const BASE_ENERGY_MAX = 20;
/** Un point d'énergie revient toutes les 15 secondes réelles. */
export const ENERGY_REGEN_SECONDS = 15;
export const SUMMER_HOURS = 100;
export const SUMMER_PAY = 15;
export const SMALL_JOB_PAY = 15;
export const BIKE_PRICE = 120;
export const BIKE_BONUS = 1.2;
export const BIKE_UNLOCK_EARNED = 150;
export const PIGGY_UNLOCK_EARNED = 100;
/** Vider son énergie plus de trois fois dans le même mois déclenche la fatigue. */
export const DEPLETIONS_BEFORE_FATIGUE = 3;
/** Chaque heure de travail en plus (petits boulots, heures en extra) coûte un point de bien-être. */
export const WORK_WELLBEING_COST = 1;
/**
 * Pendant le job d'été, un demi-point par heure (−50 sur tout l'été) : aucun mois ne passe pour faire
 * remonter la jauge, seuls les petits plaisirs achetés la font remonter, et l'été ils ne s'usent pas.
 */
export const SUMMER_WELLBEING_COST = 0.5;

export type WorkMode = 'summer' | 'smallJobs' | 'missions' | 'none';

export function workMode(state: GameState): WorkMode {
  if (state.stage === 'prologue') {
    if (state.prologueStep === 'summer') return 'summer';
    if (state.prologueStep === 'years') return 'smallJobs';
    return 'none';
  }
  return 'missions';
}

export function energyMax(state: GameState): number {
  return BASE_ENERGY_MAX + upgradeBonus(state, 'energyMax');
}

/** Gain d'une heure de travail en extra, avant les bonus des outils achetés. */
export function basePay(state: GameState): number {
  const mode = workMode(state);
  const bike = state.hasBike ? BIKE_BONUS : 1;
  if (mode === 'summer') return SUMMER_PAY * bike;
  if (mode === 'smallJobs') return SMALL_JOB_PAY * bike;
  if (mode === 'missions') return (state.salary / MONTHLY_HOURS) * OVERTIME_BONUS;
  return 0;
}

export function payPerClick(state: GameState): number {
  // Avec une voiture, on accepte des missions plus loin.
  return basePay(state) * (1 + upgradeBonus(state, 'pay') + (state.hasCar ? CAR.pay : 0));
}

/** Pourquoi on ne peut pas dépenser d'énergie en ce moment, ou null si on peut. */
export function energyBlocker(state: GameState): TextKey | null {
  if (!state.energyUnlocked) return 'work.none';
  if (state.day < state.restUntilDay) return 'work.resting';
  if (state.energy < 1) return 'work.noEnergy';
  return null;
}

/** Dépense un point d'énergie ; déclenche la fatigue en cas d'excès. */
export function spendEnergy(state: GameState): boolean {
  if (energyBlocker(state)) return false;
  const before = state.energy;
  state.energy -= 1;
  applyEnergyDrop(state, before);
  if (state.energy < 1) {
    state.depletionsThisMonth += 1;
    const alreadyTired = state.day < state.fatigueUntilDay || state.day < state.restUntilDay;
    if (state.depletionsThisMonth > DEPLETIONS_BEFORE_FATIGUE && !alreadyTired && !state.pendingEvent) {
      startEvent(state, 'fatigue');
    }
  }
  return true;
}

export function work(state: GameState): void {
  const mode = workMode(state);
  if (mode === 'none') return;
  const pay = round2(payPerClick(state));
  if (mode === 'summer') {
    state.cash = round2(state.cash + pay);
    state.summerClicks += 1;
    state.summerEarned = round2(state.summerEarned + pay);
    addWellbeing(state, -SUMMER_WELLBEING_COST);
    if (state.summerClicks >= SUMMER_HOURS) {
      state.prologueStep = 'endSummer';
      addJournal(state, 'journal.summerDone', { amount: fmtEur(state.summerEarned) });
    } else {
      // L'argent gardé en poche donne des envies.
      maybeSummerTemptation(state);
    }
    return;
  }
  if (!spendEnergy(state)) return;
  state.cash = round2(state.cash + pay);
  state.earnedByClick = round2(state.earnedByClick + pay);
  recordIncome(state, pay);
  addWellbeing(state, -WORK_WELLBEING_COST);
  checkExhaustion(state);
}

/** L'énergie revient en temps réel ; le mode développeur l'accélère aussi. */
export function regenEnergy(state: GameState, realMs: number): void {
  const max = energyMax(state);
  // Un bonus perdu (des chaussures usées) ramène l'énergie à son nouveau maximum.
  if (state.energy > max) state.energy = max;
  if (!state.energyUnlocked || state.energy >= max) return;
  // Une voiture fait gagner du temps de trajet : l'énergie revient plus vite.
  const bonus = upgradeBonus(state, 'regen') + (state.hasCar ? CAR.regen : 0);
  let perSecond = (state.speed.dev / ENERGY_REGEN_SECONDS) * (1 + bonus);
  if (state.day < state.fatigueUntilDay) perSecond *= 0.5;
  // Le palier de bien-être accélère ou ralentit le retour de l'énergie.
  perSecond *= currentMood(state).regen;
  state.energy = Math.min(max, state.energy + (perSecond * realMs) / 1000);
}

export function buyBike(state: GameState): void {
  if (state.hasBike || state.cash < BIKE_PRICE) return;
  state.cash = round2(state.cash - BIKE_PRICE);
  state.hasBike = true;
  addJournal(state, 'journal.bike');
}

export function putInPiggy(state: GameState, amount: number): void {
  const value = Math.min(amount, state.cash);
  if (value <= 0) return;
  state.cash = round2(state.cash - value);
  state.piggy = round2(state.piggy + value);
  unlockPage(state, 'se_payer_en_premier');
}
